<template>
  <div class="allergies-warning" data-testid="kds-allergies" v-if="order.order.allergies">
    <span class="warning-icon">⚠️</span>
    <div class="warning-content">
      <div class="warning-title">Alergias</div>
      <span class="warning-text" translate="no">{{ order.order.allergies }}</span>
    </div>
  </div>
  <div class="comments-notice" data-testid="kds-comment" v-if="order.order.comment">
    <span class="notice-icon">📝</span>
    <div class="notice-content">
      <div class="notice-title">Comentarios</div>
      <span class="notice-text" translate="no">{{ order.order.comment }}</span>
    </div>
  </div>
  <div class="ticket-content" v-if="filteredContent.length > 0">
    <div v-for="(item) in filteredContent" :key="item.sell_id" class="ticket-item">
        <div class="item-main" 
             data-testid="kds-item"
             @click="handleItemClick($event, item.sell_id)" 
             :class="{ 'crossed-out': isCrossedOut(item.sell_id) }">
          <p class="emoji" v-html="getFirstIcon(item)"></p>
          <span class="item-count">{{ item.quantity }}</span>
          <span class="parent-item" translate="no">{{ item.display_string }}</span>
        </div>
        <div class="item-details">
          <div v-for="subItem in item.items" 
               :key="subItem.sell_id" 
               class="sub-item"
               data-testid="kds-sub-item"
               translate="no"
               @click.stop="handleItemClick($event, subItem.sell_id)"
               :class="{ 'crossed-out': isCrossedOut(subItem.sell_id) }">
            <p class="emoji" v-html="getFirstIcon(subItem)"></p>
            {{ subItem.quantity }} x {{ subItem.display_string }}
          </div>
        </div>
    </div>
  </div>
  <div v-else class="ticket-content">
    <div class="ticket-capsule">
      <div class="ticket-header">Sin Contenido</div>
    </div>
  </div>
</template>


<script>
const uEmojiParser = require('universal-emoji-parser')
import { createOrUpdateCrossItem } from '@/graphql'; // Import the GraphQL function

export default {
  name: 'TicketComponentKDS',
  emits: ['item-clicked'],
  props: {
    order: {
      type: Object,
      required: true,
      validator(order) {
        return order && (
          typeof order.ticketContent === 'undefined' || 
          Array.isArray(order.ticketContent)
        );
      }
    },
    restaurant: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data() {
    return {
      crossedOutItems: new Set(),
      lastOrderContentUpdate: Date.now() // Add timestamp to track updates
    }
  },
  watch: {
    // Watch the entire order object to catch all changes
    order: {
      handler() {
        console.log('TicketComponentKDS - Order changed, syncing crossed out items');
        this.syncCrossedOutItems();
      },
      deep: true,
      immediate: true
    },
    // Also keep the specific watch on orderContent for more targeted updates
    'order.orderContent': {
      handler() {
        console.log('TicketComponentKDS - Order content changed, syncing crossed out items');
        this.syncCrossedOutItems();
        this.lastOrderContentUpdate = Date.now(); // Update timestamp to force reactivity
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    filteredContent() {
      if (!this.order.orderContent || !this.order.orderContent.hierarchical) {
        return [];
      }

      // If no restaurant or deviceTag is set, show all items
      if (!this.restaurant || !this.restaurant.deviceTag) {
        return this.order.orderContent.hierarchical;
      }

      // Filter items that have a tag matching the device tag
      return this.order.orderContent.hierarchical.filter(item => {
        // Include the item if it has the same tag as the device
        return item.tag === this.restaurant.deviceTag;
      });
    }
  },
  methods: {
    syncCrossedOutItems() {
      // Clear existing Set
      this.crossedOutItems.clear();
      
      // If there's no order content, return early
      if (!this.order.orderContent || !this.order.orderContent.hierarchical) {
        console.log('TicketComponentKDS - No order content to sync crossed out items');
        return;
      }
      
      // Function to recursively check items and their sub-items
      const checkItems = (items) => {
        for (const item of items) {
          if (item.sell_id && item.crossedOutBy) {
            console.log(`TicketComponentKDS - Found crossed out item: ${item.sell_id} by ${item.crossedOutBy}`);
            this.crossedOutItems.add(item.sell_id);
          }
          // Check sub-items if they exist
          if (item.items && Array.isArray(item.items)) {
            for (const subItem of item.items) {
              if (subItem.sell_id && subItem.crossedOutBy) {
                console.log(`TicketComponentKDS - Found crossed out sub-item: ${subItem.sell_id} by ${subItem.crossedOutBy}`);
                this.crossedOutItems.add(subItem.sell_id);
              }
            }
          }
        }
      };
      
      // Process all items in the hierarchical structure
      checkItems(this.order.orderContent.hierarchical);
      console.log(`TicketComponentKDS - Synced ${this.crossedOutItems.size} crossed out items`);
    },
    getFirstIcon(item) {
      if (item.icon) {
        return uEmojiParser.parse(item.icon);
      }
      return uEmojiParser.parse(':package:');
    },
    async toggleCrossOut(sellId) {
      const wasCrossedOut = this.crossedOutItems.has(sellId);
      
      // Update local state
      if (wasCrossedOut) {
        this.crossedOutItems.delete(sellId);
      } else {
        this.crossedOutItems.add(sellId);
      }
      
      try {
        // Get the restaurant tag or default to empty string
        const tag = this.restaurant?.deviceTag || '';

        // If the item was uncrossed and is now crossed out set crossedOutBy to "MANUAL"
        // If the item was crossed out and is now uncrossed set crossedOutBy to null
        const crossedOutBy = wasCrossedOut ? null : "MANUAL";
        
        // Update the database using the sell_id instead of a constructed ID
        await createOrUpdateCrossItem(sellId, tag, crossedOutBy);
      } catch (error) {
        console.error('Error updating crossed out status:', error);
        // Revert UI state if database update failed
        if (wasCrossedOut) {
          this.crossedOutItems.add(sellId);
        } else {
          this.crossedOutItems.delete(sellId);
        }
      }
    },
    isCrossedOut(sellId) {
      // First check the local Set for best performance
      if (this.crossedOutItems.has(sellId)) {
        return true;
      }
      
      // Also directly check the order content for the most up-to-date information
      if (this.order.orderContent && this.order.orderContent.hierarchical) {
        // Find the item in hierarchical content
        const findItem = (items) => {
          for (const item of items) {
            if (item.sell_id === sellId && item.crossedOutBy) {
              console.log(`TicketComponentKDS - Direct check found crossed item: ${sellId} by ${item.crossedOutBy}`);
              // Update the local set for future checks
              this.crossedOutItems.add(sellId);
              return true;
            }
            // Check sub-items
            if (item.items) {
              for (const subItem of item.items) {
                if (subItem.sell_id === sellId && subItem.crossedOutBy) {
                  console.log(`TicketComponentKDS - Direct check found crossed sub-item: ${sellId} by ${subItem.crossedOutBy}`);
                  // Update the local set for future checks
                  this.crossedOutItems.add(sellId);
                  return true;
                }
              }
            }
          }
          return false;
        };
        
        return findItem(this.order.orderContent.hierarchical);
      }
      
      return false;
    },
    async handleItemClick(event, sellId) {
      event.stopPropagation(); // Prevent event bubbling
      await this.toggleCrossOut(sellId);
      this.$emit('item-clicked', sellId); // Emit event to parent
    }
  }
};
</script>

<style scoped>
.ticket-content {
  width: 100%;
  text-align: left;
  margin-top: 12px;
  max-height: 340px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch; /* For smoother scrolling on iOS */
  padding-right: 4px; /* Add space for scrollbar */
}

.ticket-content::-webkit-scrollbar {
  width: 4px;
}

.ticket-content::-webkit-scrollbar-track {
  background: var(--bz-p-white-a10);
  border-radius: 2px;
}

.ticket-content::-webkit-scrollbar-thumb {
  background-color: var(--bz-p-white-a30);
  border-radius: 2px;
}

.ticket-item {
  margin-bottom: 8px;
  padding: 8px;
  background: var(--bz-p-white-a10);
  border-radius: 16px;
}

.item-main {
  display: flex;
  align-items: center;
  background: var(--bz-p-3f95ff);
  border-radius: 12px;
  margin-bottom: 4px;
  padding: 8px;
}

.emoji {
  width: 16px;
  height: 16px;
  margin-right: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.parent-item {
  flex-grow: 1;
  text-align: left;
  font-weight: 600;
  font-size: 16px;
  color: var(--bz-p-0c1117);
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  overflow: hidden;
}

.item-count {
  background: var(--bz-p-white-a20);
  border-radius: 999px;
  padding: 1px 6px;
  font-size: 16px;
  color: var(--bz-p-0c1117);
  margin-right: 6px;
  font-weight: 800;
}

.item-details {
  flex-grow: 1;
  text-align: left;
  margin-left: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sub-item {
  display: flex;
  align-items: center;
  background: var(--bz-p-blue-400-a15);
  font-size: 15px;
  color: var(--bz-p-0c1117);
  border-radius: 12px;
  padding: 8px;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  flex-wrap: wrap;
}

.sub-item .emoji {
  flex-shrink: 0;
}

.allergies-warning,
.comments-notice {
  width: 100%;
  background: var(--bz-p-ff5252-a10);
  border: 1px solid var(--bz-p-ff5252-a30);
  border-radius: 16px;
  padding: 12px 12px 12px 25px;
  margin-bottom: 8px;
  display: flex;
  align-items: flex-start;
  color: var(--bz-p-ff5252);
  backdrop-filter: blur(8px);
}

.warning-icon {
  width: 20px;
  height: 20px;
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.warning-content {
  flex-grow: 1;
}

.warning-title {
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.5px;
  text-align: left;
}

.warning-text {
  font-weight: 500;
  font-size: 14px;
  line-height: 1.4;
  font-style: italic;
  display: block;
}

.comments-notice {
  background: var(--bz-p-ffc107-a10);
  border: 1px solid var(--bz-p-ffc107-a30);
  color: var(--bz-ink--ffc107);
}

.notice-icon {
  width: 20px;
  height: 20px;
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notice-content {
  flex-grow: 1;
}

.notice-title {
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.5px;
  text-align: left;
}

.notice-text {
  font-weight: 500;
  font-size: 14px;
  line-height: 1.4;
  font-style: italic;
  display: block;
}

/* Tablet styles */
@media (min-width: 768px) and (max-width: 1024px) {
  .ticket-content {
    max-height: 320px;
  }
}

/* Mobile styles */
@media (max-width: 767px) and (orientation: portrait) {
  .ticket-content {
    max-height: 300px;
  }
  
  .ticket-capsule {
    margin-bottom: 12px;
    padding: 6px;
  }

  .item-main {
    padding: 6px;
  }

  .item-details {
    margin-left: 12px;
  }

  .sub-item {
    padding: 4px;
  }

  .emoji {
    width: 14px;
    height: 14px;
    margin-right: 4px;
  }

  .allergies-warning,
  .comments-notice {
    padding: 8px 8px 8px 12px;
  }
  
  .warning-icon,
  .notice-icon {
    width: 16px;
    height: 16px;
    margin-right: 6px;
  }
}

.crossed-out {
  text-decoration: line-through;
  opacity: 0.6;
  transition: all 0.2s ease;
}

.item-main, .sub-item {
  cursor: pointer;
  transition: all 0.2s ease;
}

.item-main:hover, .sub-item:hover {
  filter: brightness(0.95);
}

.item-main:active, .sub-item:active {
  transform: scale(0.98);
}
</style>